import React from "react";
import { useSelector } from "react-redux";

import { Column, format, Row } from "@gamedev-web/gamedev-sdk-core";

import { useString, useStrings } from "../../localization/localizationHelpers";
import { getOnboardingSignedIn } from "../../redux/selectors/authSelectors";
import { RightPaneBox } from "../RightPaneBox";
import * as styles from "./ProgressBar.module.css";
import { useAaaPercentage } from "./useAaaPercentage";
import { useIdAzurePercentage } from "./useIdAzurePercentage";
import { useIdXboxGameConceptPercentage, useIdXboxWelcomePercentage } from "./useIdXboxPercentage";
import { usePitchPercentage } from "./usePitchPercentage";

const cssWidthPercentageFormat = "{0}%";

export type ProgressBarProps = {
  percentage: number;
  customTitle?: string;
  customFooter?: string;
};

const ProgressBar = ({ percentage, customTitle, customFooter }: ProgressBarProps) => {
  const [progressBarTitle, progressBarPercentageTextFormat, progressBarFootnote] = useStrings(
    "progressBarTitle",
    "progressBarPercentageTextFormat",
    "progressBarFootnote"
  );
  const isSignedIn = useSelector(getOnboardingSignedIn);

  if (!isSignedIn) {
    return null;
  }

  const percentageText = format(progressBarPercentageTextFormat, Math.floor(percentage).toString());
  return (
    <RightPaneBox>
      <Row>
        <Column>
          <h2 className={styles.title}>{customTitle ?? progressBarTitle}</h2>
        </Column>
        <Column>
          <p className={styles.percentageText}>{percentageText}</p>
        </Column>
      </Row>
      <Row>
        <Column>
          <div className={styles.bar}>
            <div
              className={styles.barFill}
              style={{ width: format(cssWidthPercentageFormat, percentage.toString()) }}
            />
          </div>
          <p className={styles.footnote}>{customFooter ?? progressBarFootnote}</p>
        </Column>
      </Row>
    </RightPaneBox>
  );
};

export const IdXboxGameConceptProgressBar = () => {
  const progressBarPercentage = useIdXboxGameConceptPercentage();
  return <ProgressBar percentage={progressBarPercentage} />;
};

export const IdXboxWelcomeProgressBar = () => {
  const progressBarPercentage = useIdXboxWelcomePercentage();
  return <ProgressBar percentage={progressBarPercentage} />;
};

export const IdAzureProgressBar = () => {
  const progressBarPercentage = useIdAzurePercentage();
  return <ProgressBar percentage={progressBarPercentage} />;
};

export const PitchProgressBar = () => {
  const pitchProgressBarFootnote = useString("pitchProgressBarFootnote");
  const progressBarPercentage = usePitchPercentage();
  return <ProgressBar percentage={progressBarPercentage} customFooter={pitchProgressBarFootnote} />;
};

export const AaaProgressBar = () => {
  const [aaaProgressBarFootnote, aaaGameConceptProgressBarTitle] = useStrings(
    "aaaProgressBarFootnote",
    "aaaGameConceptProgressBarTitle"
  );
  const progressBarPercentage = useAaaPercentage();
  return (
    <ProgressBar
      percentage={progressBarPercentage}
      customTitle={aaaGameConceptProgressBarTitle}
      customFooter={aaaProgressBarFootnote}
    />
  );
};
